import * as AppConstants from "AppConstants";
import { isEmpty } from "utils/validationUtil";
import { handleAxiosError } from "app/services/app.error.handler";
import { getMemberByUserId } from "app/services/member.service";
import { getManagerByUserId } from "app/services/manager.service";
import * as sessionStorageProxy from "app/services/app.session.storage.proxy";
import { loadClientByClientUserId, loadClientsForBdm } from "app/services/client.service";


/**
 * The /by-user/{userId} endpoint returns ALL memberships for a user (a user may be
 * mapped to multiple clients). Until a client switcher exists, the active membership
 * is the first one. Defensive against the legacy single-object shape.
 */
const selectActiveMember = (data) => {
    if (Array.isArray(data)) {
        return data.length > 0 ? data[0] : null;
    }
    return data; // legacy single-object response, kept for safety during rollout
};

var isLogin = readSessionIsUserLoggedIn();
var loggedInUser = readSessionLoggedInUser();

const init = () => {
    isLogin = readSessionIsUserLoggedIn();
    loggedInUser = readSessionLoggedInUser();
}

export function readSessionToken() {
    return sessionStorageProxy.getItem(AppConstants.SESSION_KEY_TOKEN, false, true);
} 

export function readSessionLoggedInUser() {
    return sessionStorageProxy.getItem(AppConstants.SESSION_KEY_LOGGED_IN_USER, true, true);
} 

export function readSessionIsUserLoggedIn() {
    return sessionStorageProxy.getItem(AppConstants.SESSION_KEY_IS_LOGIN, false, false);
} 

export function readSessionCurrentClient() {
    return sessionStorageProxy.getItem(AppConstants.SESSION_KEY_CURRENT_CLIENT, true, true);
} 

export function readSessionCurrentManager() {
    return sessionStorageProxy.getItem(AppConstants.SESSION_KEY_CURRENT_MANAGER, true, true);
} 

export function readSessionCurrentMember() {
    return sessionStorageProxy.getItem(AppConstants.SESSION_KEY_CURRENT_MEMBER, true, true);
} 

export function isAuthenticated() {
    init();
    return !isEmpty(isLogin) && isLogin === 'true' && !isEmpty(loggedInUser);
}

/**
 * Checks if user has logged in and returns all roles for logged in user
 * @returns all roles for logged in user
 * @returns empty array otherwise
 */
export function getLoggedInUserRoles() {
    init();
    if(!isEmpty(isLogin) && !isEmpty(loggedInUser)) {
        return loggedInUser.roles.map(roleObj => roleObj.role);
    }

    return [];
}

export function getLoggedInUserId() {
    init();
    if(!isEmpty(isLogin) && !isEmpty(loggedInUser)) {
        return loggedInUser.id;
    }
}

export function getLoggedInUser() {
    init();
    if(!isEmpty(isLogin) && !isEmpty(loggedInUser)) {
        return loggedInUser;
    }
}

export function getLoggedInUserNameAndEmail() {
    init();
    if(!isEmpty(isLogin) && !isEmpty(loggedInUser)) {
        var user = loggedInUser;
        return `${user.username} <${user.email}>`
    }
}

export function getLoggedInUserInitials() {
    init();
    if(!isEmpty(isLogin) && !isEmpty(loggedInUser)) {
        var username = loggedInUser.username;
        return username.charAt(0);

    }
    return "";
}

export function getLoggedInUserFullName() {
    init();
    if(!isEmpty(isLogin) && !isEmpty(loggedInUser)) {
        const raw = typeof loggedInUser.fullName === "string" ? loggedInUser.fullName : "";
        const trimmed = raw.trim();
        return trimmed || loggedInUser.username || "";
    }
    return "";
}

/**
 * Checks if a user has logged in and has given role
 * @returns true if a user has given role
 * @returns false otherwise
 */
export function checkUserHasRole(role) {
    init();
    if(!isEmpty(isLogin) && !isEmpty(loggedInUser)) {
        const user = loggedInUser;
        return (user.roles.some(roleObj => roleObj.role === role));
    }

    return false;
}

/**
 * Checks if a user has logged in and has Member role
 * @returns true if a user has Member role
 * @returns false otherwise
 */
export function checkUserHasMemberRole() {
    return checkUserHasRole(AppConstants.ROLE_EMPLOYEE_MEMBER);
}

/**
 * Checks if a user has Admin role
 * @returns true if a user has Admin role
 * @returns false otherwise
 */
export function checkUserHasAdminRole() {
    return checkUserHasRole(AppConstants.ROLE_ADMIN);
}

/**
 * Checks if a user has Master Data Admin role
 * @returns true if a user has Master Data Admin role
 * @returns false otherwise
 */
export function checkUserHasMasterDataAdminRole() {
    return checkUserHasRole(AppConstants.ROLE_MASTER_DATA_ADMIN);
}

/**
 * The screen a signed-in user should land on. Master Data Admin has no dashboard,
 * so sending them via /dashboard makes them bounce off it to /masters.
 * @returns the landing route path for the logged in user
 * @since NEXT_VERSION
 */
export function getLandingRoute() {
    if (checkUserHasMasterDataAdminRole()) {
        return AppConstants.ROUTE_MASTERS;
    }
    // A BDM lands on the dashboard like everyone else. They used to be sent to the members view,
    // which HRMS-116's rewrite removes from their menu -- leaving them on a route their own role
    // can no longer reach, and a blank screen after login. @since NEXT_VERSION
    return AppConstants.ROUTE_DASHBOARD;
}

/**
 * Checks if a user has logged in and has Manager role
 * @returns true if a user has Manager role
 * @returns false otherwise
 */
export function checkUserHasManagerRole() {
    return checkUserHasRole(AppConstants.ROLE_EMPLOYEE_MANAGER);
}

/**
 * Checks if a user has logged in and has a Client role
 * @returns true if a user has Client role
 * @returns false otherwise
 */
 export function checkUserHasClientRole() {
    return checkUserHasRole(AppConstants.ROLE_CLIENT);
}

/**
 * Checks if a user has logged in and has a Account role
 * @returns true if a user has Account role
 * @returns false otherwise
 */
 export function checkUserHasAccountRole() {
    return checkUserHasRole(AppConstants.ROLE_ACCOUNT);
}

/**
 * Checks if a user has logged in and has the BDM (Business Development Manager) role
 * @returns true if a user has BDM role
 * @returns false otherwise
 * @since NEXT_VERSION — HRMS-116
 */
 export function checkUserHasBdmRole() {
    return checkUserHasRole(AppConstants.ROLE_BDM);
}

/**
 * Checks if user has logged in and has any of the required roles
 * @param {*} requiredRoles 
 * @returns true if user has any of the required roles
 * @returns false otherwise
 */
export function checkUserHasSomeRole(requiredRoles) {
    init();
    if(!isEmpty(isLogin) && !isEmpty(loggedInUser)) {
        const user = loggedInUser;
        return (user.roles.some(roleObj => requiredRoles.some (role => roleObj.role === role)));
    }

    return false;
}

/**
 * Checks if user has logged in and has any of Admin Or Client Or Manager role
 * @returns true if user has any of Admin Or Client Or Manager role
 * @returns false otherwise
 */
export function checkUserHasAdminOrClientOrManagerRole() {
    return checkUserHasSomeRole([AppConstants.ROLE_ADMIN, AppConstants.ROLE_CLIENT, AppConstants.ROLE_EMPLOYEE_MANAGER]);
}

/**
 * Checks if user has logged in and has  all of the required roles
 * @param {*} requiredRoles 
 * @returns true if user has all of the required roles
 * @returns false otherwise
 */
export function checkUserHasAllRoles(requiredRoles) {
    init();
    if(!isEmpty(isLogin) && !isEmpty(loggedInUser)) {
        const user = loggedInUser;
        return (requiredRoles.every(roleObj => user.roles.some (role => roleObj.role === role)));
    }

    return false;
}

/**
 * Checks if user has logged in and has Member role then returns memberId associated with userId
 * @returns member id for current logged in user
 */
export const getCurrentMemberId = () => {
    return new Promise((resolve, reject) => {
        if(!checkUserHasMemberRole()) {
            reject(new Error("You do not have access to getCurrentMemberId functionality."));;
        }
        else {
            const member = readSessionCurrentMember();
            if(!isEmpty(member)) {
                resolve(member.id);
            }
            else {
                const user = readSessionLoggedInUser();
                if(isEmpty(user) || user?.id<= 0) {
                    reject(new Error("Empty user details"));
                }
                else {
                    return getMemberByUserId(user.id)
                        .then(response => selectActiveMember(response.data))
                        .then(member => {
                            if (isEmpty(member)) {
                                reject(new Error("No member record found for user"));
                                return;
                            }
                            sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_MANAGER, JSON.stringify(member));
                            resolve(member.id);
                        })
                        .catch(error=>{
                            handleAxiosError(error);
                            reject(new Error("Error while fetching member ID"));
                        });
                }
            }
        }
    });
}

/**
 * Checks if user has logged in and has Member role then returns member record associated with userId
 * @returns member object for current logged in user
 */
export const getCurrentMember = () => {
    return new Promise((resolve, reject) => {
        if(!checkUserHasMemberRole()) {
            reject(new Error("You do not have access to getCurrentMember functionality."));
        }
        else {
            var memberFromSession = readSessionCurrentMember();
            if(!isEmpty(memberFromSession)) {
                resolve(memberFromSession);
            }
            else {
                // Get member from db
                const user = readSessionLoggedInUser();
                return getMemberByUserId(user.id)
                    .then(response => selectActiveMember(response.data))
                    .then(member => {
                        if (isEmpty(member)) {
                            reject(new Error("No member record found for user"));
                            return;
                        }
                        sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_MEMBER, JSON.stringify(member));
                        sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_CLIENT, JSON.stringify(member.client));
                        resolve(member);
                    })
                    .catch(error=>{
                        handleAxiosError(error);
                        reject(new Error("Error while fetching member data"));
                    });
            }
        }
    })
}

/**
 * Returns ALL memberships (one per client) for the logged-in member. Cached in session under
 * SESSION_KEY_MEMBER_CLIENTS after the first fetch. Resolves [] when there is no logged-in user
 * or the fetch fails — callers treat an empty list as "no client switcher".
 */
export const getMemberClients = () => {
    return new Promise((resolve) => {
        const cached = sessionStorageProxy.getItem(AppConstants.SESSION_KEY_MEMBER_CLIENTS, true, true);
        if (Array.isArray(cached) && cached.length > 0) {
            resolve(cached);
            return;
        }

        const user = readSessionLoggedInUser();
        if (isEmpty(user) || user?.id <= 0) {
            resolve([]);
            return;
        }

        getMemberByUserId(user.id)
            .then((response) => (Array.isArray(response.data) ? response.data : []))
            .then((members) => {
                sessionStorageProxy.setItem(AppConstants.SESSION_KEY_MEMBER_CLIENTS, JSON.stringify(members));
                resolve(members);
            })
            .catch((error) => {
                handleAxiosError(error);
                resolve([]);
            });
    });
};

/**
 * Persists the chosen membership as the active member context. The caller reloads the page
 * afterward so the dashboard re-reads SESSION_KEY_CURRENT_MEMBER / SESSION_KEY_CURRENT_CLIENT on mount.
 */
export const switchMemberClient = (membership) => {
    if (isEmpty(membership)) return;
    sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_MEMBER, JSON.stringify(membership));
    sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_CLIENT, JSON.stringify(membership.client));
};

/**
 * Checks if user has logged in and has Manager role then returns manager record associated with userId
 * @returns manager object for current logged in user
 */
 export const getCurrentManager = () => {
    return new Promise((resolve, reject) => {
        if(!checkUserHasManagerRole()) {
            reject(new Error("You do not have access to getCurrentManager functionality."));
        }
        else {
            var managerFromSession = readSessionCurrentManager();

            if(!isEmpty(managerFromSession)) {
                resolve(managerFromSession[0]);
            }
            else {
                const userFromSession = readSessionLoggedInUser();
    
                if(isEmpty(userFromSession)) {
                    reject(new Error("Empty user details"));
                }
                else {
                    return getManagerByUserId(userFromSession.id)
                        .then(response => response.data)
                        .then(manager => {
                            sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_MANAGER, JSON.stringify(manager));
                            resolve(manager);
                        })
                        .catch(error=>{
                            handleAxiosError(error);
                            reject(new Error("Error while fetching manager data"));
                        });
                }
            }
        }
    });
}

/**
 * Returns the clients/accounts assigned to the logged-in BDM. Resolves [] when there is no
 * logged-in BDM or the fetch fails — callers treat an empty list as "no assigned accounts".
 * @since NEXT_VERSION — HRMS-116
 */
export const getBdmClients = () => {
    return new Promise((resolve) => {
        if (!checkUserHasBdmRole()) {
            resolve([]);
            return;
        }
        const user = readSessionLoggedInUser();
        if (isEmpty(user) || user?.id <= 0) {
            resolve([]);
            return;
        }
        loadClientsForBdm(user.id)
            .then((response) => (Array.isArray(response.data) ? response.data : []))
            .then(resolve)
            .catch((error) => {
                handleAxiosError(error);
                resolve([]);
            });
    });
};

export const getCurrentClient = () => {
    return new Promise((resolve, reject) => {
        if(!checkUserHasClientRole()) {
            reject(new Error("You do not have access to getCurrentClient functionality."));
        }
        else {
            var clientFromSession = readSessionCurrentClient();

            if(!isEmpty(clientFromSession)) {
                resolve(clientFromSession);
            }
            else {
                const userFromSession = readSessionLoggedInUser();

                if(isEmpty(userFromSession)) {
                    reject(new Error("Empty user details"));
                }
                else {
                    return loadClientByClientUserId(userFromSession.id)
                        .then(response => response.data)
                        .then(client => {
                            sessionStorageProxy.setItem(AppConstants.SESSION_KEY_CURRENT_CLIENT, JSON.stringify(client));
                            resolve(client);
                        })
                        .catch(error=>{
                            handleAxiosError(error);
                            reject(new Error("Error while fetching client data"));
                        });
                }
            }
        }
    });
}